import { equal, handleAxiosError } from '@/api/helpers';
import { getFiltersFromColumns } from '@/client/helpers';
import { SecurityCultureSurvey } from '@/client/security-culture/types';
import { Actions, Subjects } from '@/client/users';
import {
  RedirectPaths,
  RedirectPathsEnum,
  TableNamesEnum,
} from '@/common/constants';
import { DialogContext } from '@/common/context';
import {
  DataTable,
  DataTableActions,
  DataTableColumnsMultiselect,
  DataTableColumnType,
  DataTableFilters,
  DataTableToolbar,
} from '@/components/tables/crud';
import {
  useDeleteSurvey,
  useSurveys,
} from '@/hooks/query/security-culture.hooks';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { DateFormats, DialogType } from '@/system-settings/enums';
import { AppChip } from '@/ui/chip';
import { FormatDate } from '@/ui/date';
import { FlexContainer } from '@/ui/styled-ui';
import {
  displayMiltipleItems,
  getSurveyDisplayStatusChip,
} from '@/utils/helpers/ui.helper';
import { AxiosError } from 'axios';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { MenuItem } from 'primereact/menuitem';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const SurveysDatatableV2: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = usePermission();
  const navigate = useNavigate();
  const currentAccount = useAppSelector(selectCurrentAccount);

  const { skip, take, sort, apiFilters, onFilter, onSort, onPage } = useTable({
    tableName: TableNamesEnum.SURVEYS_LIST,
  });

  const { setDialogData } = useContext(DialogContext);

  const { isLoading, surveys, refetch, isFetching } = useSurveys(
    {
      take,
      skip,
      filters: [...apiFilters, equal('isDemo', false)],
      sort: sort && sort.length > 0 ? [sort.join(',')] : [],
    },
    currentAccount?.id,
  );

  const onRowClick = (event: DataTableRowClickEvent) => {
    if (event.data.isDemo || isLoading) return;
    navigate(
      RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_EDIT_SURVEY](
        event.data.id,
      ),
    );
  };

  const formatTargetColumn = (row: SecurityCultureSurvey) => {
    const targets = Object.values(row?.targets || []);

    return targets?.length ? (
      <div className="align-self-center">
        {displayMiltipleItems(
          targets
            ?.flat()
            ?.map(({ name }) => name)
            ?.join(', '),
          t,
        )}
      </div>
    ) : (
      <div className="align-self-center">{currentAccount?.name}</div>
    );
  };

  const columns: DataTableColumnType[] = [
    {
      field: 'targets',
      header: t('security.culture.target.group'),
      sortable: false,
      filterable: false,
      render: (row: SecurityCultureSurvey) => (
        <FlexContainer justify="flex-start" gap={24}>
          {row?.isDemo && (
            <AppChip label={t('security.culture.demo.status')} type="error" />
          )}
          {formatTargetColumn(row)}
        </FlexContainer>
      ),
    },
    {
      field: 'totalParticipants',
      header: t('security.culture.number.of.users'),
      sortable: true,
      filterable: false,
      style: { width: '165px' },
      render: (row: SecurityCultureSurvey) =>
        row?.isDemo ? <span>&#8212;</span> : row?.totalParticipants || 0,
    },
    {
      field: 'date',
      header: t('generic.date'),
      sortable: true,
      filterable: false,
      render: (row: SecurityCultureSurvey) => {
        const displayDate = row?.scheduledFor || row?.date;

        return row?.isDemo ? (
          <span>&#8212;</span>
        ) : displayDate ? (
          <FormatDate date={displayDate} format={DateFormats.TIMEDATE} />
        ) : (
          t('generic.never')
        );
      },
    },
    {
      field: 'scheduleStatus',
      header: t('generic.status'),
      sortable: false,
      filterable: false,
      style: { width: '222px' },
      render: (row: SecurityCultureSurvey) => {
        if (row?.isDemo) return <span>&#8212;</span>;
        return getSurveyDisplayStatusChip(row?.displayStatus, t);
      },
    },
    {
      field: 'actions',
      header: t('generic.actions'),
      sortable: false,
      filterable: false,
      style: { width: '80px', textAlign: 'center' },
      render: (row: SecurityCultureSurvey) => (
        <DataTableActions menuItems={menuItems(row)} />
      ),
    },
  ];

  // Set the preselected columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

  const defaultVisibleColumns = columns.map((column) => column.field);
  const alwaysVisibleColumns = ['targets', 'actions'];
  const toolbar = (
    <DataTableToolbar>
      <FlexContainer
        justify="space-between"
        gap={8}
        align="flex-start"
        wrap="wrap"
      >
        {!currentAccount?.isSystem && (
          <DataTableFilters
            filters={getFiltersFromColumns(columns)}
            onFilter={onFilter}
            tableName={TableNamesEnum.SURVEYS_LIST}
          />
        )}
        <DataTableColumnsMultiselect
          columns={columns}
          tableName={TableNamesEnum.SURVEYS_LIST}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          defaultVisibleColumns={defaultVisibleColumns}
          alwaysVisibleColumns={alwaysVisibleColumns}
        />
      </FlexContainer>
    </DataTableToolbar>
  );

  const deleteSurvey = useDeleteSurvey();
  const handleDeleteSchedule = async (survey: SecurityCultureSurvey) => {
    if (!currentAccount?.id || !survey?.id) return;

    try {
      await deleteSurvey.delete({
        accountId: currentAccount?.id,
        surveyId: survey?.id,
      });
      await refetch();

      toast?.success(t('toast.success'), t('security.culture.survey.deleted'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const menuItems = (survey: SecurityCultureSurvey) => {
    const menu: MenuItem[] = [
      {
        label: t('generic.edit'),
        icon: 'pi pi-pencil',
        command: () => {
          navigate(
            RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_EDIT_SURVEY](
              survey.id as string,
            ),
          );
        },
      },
    ];

    if (can(Actions.DELETE, Subjects.SECURITY_CULTURE)) {
      menu.push({
        label: t('generic.delete'),
        icon: 'pi pi-times',
        command: () =>
          setDialogData({
            type: DialogType.CONFIRMATION,
            show: true,
            header: t('dialog.delete'),
            message: t('dialog.delete.survey'),
            onAccept: () => handleDeleteSchedule(survey),
          }),
      });
    }

    return menu;
  };

  return (
    <>
      <DataTable
        data={surveys?.result}
        count={surveys?.count}
        isLoading={isLoading}
        isFetching={isFetching}
        columns={columns}
        visibleColumns={visibleColumns}
        toolbar={toolbar}
        rows={take}
        skip={skip}
        onPage={(surveys?.count ?? 0) > take ? onPage : undefined}
        onSort={onSort}
        sort={sort}
        onRowClick={onRowClick}
      />
    </>
  );
};
